import React, { FC } from 'react';
import {
  Card,
  CardContent,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { PageStatusResult } from '../pageStatusTypes';
import {
  getDisplayUrl,
  getEffectiveUrlDisplay,
  getSortedDetailedResults,
  getStatusChipColor,
  getStatusLabel,
} from '../pageStatusUtils';
import { PageStatusResponseTimeBadge } from './PageStatusResponseTimeBadge';
import { PageStatusResultUrlCell } from './PageStatusResultUrlCell';
import { PageStatusSectionTitle } from './PageStatusSectionTitle';

type PageStatusDetailedResultsTableProps = {
  title: string;
  helper?: string;
  results: PageStatusResult[];
};

export const PageStatusDetailedResultsTable: FC<PageStatusDetailedResultsTableProps> = ({ title, helper, results }) => {
  const sortedResults = getSortedDetailedResults(results);

  return (
    <Card variant='outlined' sx={{ mb: 3, borderRadius: 2, maxWidth: '100%', overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        <PageStatusSectionTitle title={title} helper={helper} />

        {sortedResults.length === 0 ? (
          <Typography variant='body2' color='text.secondary'>
            Ingen resultater ennå.
          </Typography>
        ) : (
          <TableContainer
            component={Paper}
            variant='outlined'
            sx={{
              maxHeight: { xs: 480, sm: 650 },
              width: '100%',
              maxWidth: '100%',
              overflowX: 'auto',
            }}>
            <Table size='small' stickyHeader sx={{ minWidth: { xs: 560, sm: 760 } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: { xs: 86, sm: 110 } }}>Status</TableCell>
                  <TableCell sx={{ minWidth: { xs: 250, sm: 340 } }}>URL</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Endelig URL / feil</TableCell>
                  <TableCell align='right' sx={{ width: { xs: 116, sm: 140 } }}>
                    Responstid
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedResults.map((result) => {
                  const details = result.error || getEffectiveUrlDisplay(result);

                  return (
                    <TableRow key={`${getDisplayUrl(result)}-${result.status_code}-${result.response_time}`} hover>
                      <TableCell sx={{ width: { xs: 86, sm: 110 } }}>
                        <Chip
                          label={getStatusLabel(result.status_code)}
                          color={getStatusChipColor(result.status_code)}
                          size='small'
                          variant='outlined'
                        />
                      </TableCell>

                      <TableCell sx={{ minWidth: { xs: 250, sm: 340 } }}>
                        <PageStatusResultUrlCell result={result} />
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' }, maxWidth: 460 }}>
                        <Typography
                          variant='body2'
                          color='text.primary'
                          sx={{
                            overflowWrap: 'anywhere',
                          }}>
                          {details || '-'}
                        </Typography>
                      </TableCell>

                      <TableCell align='right' sx={{ width: { xs: 116, sm: 140 } }}>
                        <PageStatusResponseTimeBadge responseTime={result.response_time} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};
